import { useState, useEffect, useRef } from "react";
import "../../assets/css/FollowUpLead/FollowUpLead.css";
import { MdClose } from "react-icons/md";
import {
  FaPaperclip,
  FaFileAlt,
  FaFileUpload,
  FaTimes,
  FaTrashAlt,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
} from "react-icons/fa";
import { LEAD_STATUSES } from "../Common/LeadStatus";
import { DEAL_STATUSES } from "../Common/DealStatus";
import Swal from "sweetalert2";
import api from "../../api/axios";

const EMPTY_USERS_LIST = [];

export default function FollowUpModal({
  id,
  type, // "lead" | "deal"
  isOpen,
  onClose,
  onSuccess,
  usersList = EMPTY_USERS_LIST,
  initialData = null,
}) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState(usersList);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const STATUS_OPTIONS =
    type === "lead"
      ? LEAD_STATUSES
      : DEAL_STATUSES;

  const [formData, setFormData] = useState({
    remarks: "",
    next_followup_date: "",
    next_followup_time: "",
    status: type === "lead" ? LEAD_STATUSES[0] : DEAL_STATUSES[0],
    type: "Call",
    assigned_to: "",
    actual_expense: 0,
    doc_type: "PO",
    doc_number: "",
    doc_file: null,
  });

  useEffect(() => {
    if (usersList && usersList.length > 0) {
      setUsers(usersList);
      return;
    }

    if (!isOpen) return;

    let isMounted = true;
    api.get?.("/users")
      ?.then((res) => {
        if (isMounted && res?.data && Array.isArray(res.data)) {
          setUsers(res.data);
        }
      })
      ?.catch(() => { });

    return () => {
      isMounted = false;
    };
  }, [isOpen, usersList]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        remarks: initialData.remarks || "",
        next_followup_date: initialData.next_followup_date
          ? String(initialData.next_followup_date).split("T")[0]
          : "",
        next_followup_time: initialData.next_followup_time || "",
        type: initialData.type || "Call",
        assigned_to: initialData.assigned_to ? String(initialData.assigned_to) : "",
        status: initialData.status || (type === "lead" ? LEAD_STATUSES[0] : DEAL_STATUSES[0]),
        actual_expense: initialData.actual_expense || 0,
        doc_type: initialData.status === "Vendor PO" ? "PO" : (initialData.status === "Client Invoicing" ? "INVOICE" : "PO"),
        doc_number: "",
        doc_file: null,
      });
    } else {
      const initialStatus = type === "lead" ? LEAD_STATUSES[0] : DEAL_STATUSES[0];
      setFormData({
        remarks: "",
        next_followup_date: "",
        next_followup_time: "",
        type: "Call",
        assigned_to: "",
        status: initialStatus,
        actual_expense: 0,
        doc_type: initialStatus === "Vendor PO" ? "PO" : (initialStatus === "Client Invoicing" ? "INVOICE" : "PO"),
        doc_number: "",
        doc_file: null,
      });
    }
  }, [id, type, initialData]);

  const handleChange = (e) => {
    let { name, value, files } = e.target;

    if (files) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
      return;
    }

    if (name === "actual_expense") {
      const rawVal = value.replace(/,/g, "").replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: rawVal,
      }));
      return;
    }

    if (name === "status") {
      const defaultDocType = value === "Vendor PO" ? "PO" : (value === "Client Invoicing" ? "INVOICE" : "PO");
      setFormData((prev) => ({
        ...prev,
        status: value,
        doc_type: prev.doc_type || defaultDocType,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isEdit = Boolean(initialData?.id);

  const saveFollowup = async () => {
    try {
      setLoading(true);

      if (!isClosed && !formData.next_followup_date) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Next Follow Up Date is required.",
        });
        setLoading(false);
        return;
      }

      const payload = {
        remarks: formData.remarks,
        status: formData.status,
        type: formData.type,
        assigned_to: formData.assigned_to ? Number(formData.assigned_to) : null,
        next_followup_date:
          formData.next_followup_date || (isClosed ? new Date().toISOString().split("T")[0] : null),
        next_followup_time:
          formData.next_followup_time || null,
      };

      if (type === "deal") {
        payload.actual_expense =
          formData.status === "Won Close" || formData.status === "Won"
            ? Number(formData.actual_expense) || 0
            : 0;
        payload.lost_reason =
          formData.status === "Lost" || formData.status === "Deal Lost"
            ? formData.remarks || null
            : null;
      }

      if (type === "deal" && formData.doc_file) {
        const formDataPayload = new FormData();
        formDataPayload.append("deal_id", String(id));
        formDataPayload.append("remarks", formData.remarks || "");
        formDataPayload.append("status", formData.status || "");
        formDataPayload.append("type", formData.type || "Call");
        if (formData.assigned_to) {
          formDataPayload.append("assigned_to", String(formData.assigned_to));
        }
        if (formData.next_followup_date) {
          formDataPayload.append("next_followup_date", formData.next_followup_date);
        }
        if (formData.next_followup_time) {
          formDataPayload.append("next_followup_time", formData.next_followup_time);
        }
        if (formData.status === "Won Close" || formData.status === "Won") {
          formDataPayload.append("actual_expense", String(formData.actual_expense || 0));
        }
        if (formData.status === "Lost" || formData.status === "Deal Lost") {
          formDataPayload.append("lost_reason", formData.remarks || "");
        }

        // Document attachment & metadata
        formDataPayload.append("file", formData.doc_file);
        formDataPayload.append(
          "doc_type",
          formData.doc_type || (formData.status === "Vendor PO" ? "PO" : (formData.status === "Client Invoicing" ? "INVOICE" : "PO"))
        );
        if (formData.doc_number) formDataPayload.append("doc_number", formData.doc_number);
        formDataPayload.append("doc_notes", formData.remarks || "Attached in follow-up");

        if (isEdit) {
          await api.put(`/deal-followup/${initialData.id}`, formDataPayload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await api.post("/deal-followup/create", formDataPayload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } else {
        if (isEdit) {
          const UPDATE_API_MAP = {
            lead: `/lead-followup/update/${initialData.id}`,
            deal: `/deal-followup/${initialData.id}`,
          };
          if (type === "lead") {
            payload.lead_id = Number(id) || id;
          } else {
            payload.deal_id = Number(id) || id;
          }
          await api.put(UPDATE_API_MAP[type], payload);
        } else {
          const CREATE_API_MAP = {
            lead: "/lead-followup/create",
            deal: "/deal-followup/create",
          };
          payload[type === "lead" ? "lead_id" : "deal_id"] = Number(id) || id;
          await api.post(CREATE_API_MAP[type], payload);
        }
      }

      await Swal.fire({
        icon: "success",
        title: isEdit ? "Follow Up Updated" : "Follow Up Added",
        timer: 1500,
        showConfirmButton: false,
      });

      const isConvertedStatus =
        formData.status === "Deal Confirmation" ||
        formData.status === "Converted";

      onSuccess?.({
        status: formData.status,
        isConverted: isConvertedStatus,
        type,
        id,
      });
      onClose?.();
    } catch (err) {
      console.error(err);

      let message = `Failed to ${isEdit ? "update" : "create"} follow up`;

      const data = err.response?.data;

      if (typeof data?.detail === "string") {
        message = data.detail;
      }
      else if (Array.isArray(data?.detail)) {
        message = data.detail
          .map((e) => e.msg || JSON.stringify(e))
          .join("\n");
      }
      else if (typeof data?.detail === "object" && data.detail !== null) {
        message = Object.entries(data.detail)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key}: ${value.join(", ")}`;
            }
            return `${key}: ${value}`;
          })
          .join("\n");
      }
      else if (typeof data?.message === "string") {
        message = data.message;
      }
      else if (data && typeof data === "object") {
        message = Object.entries(data)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key}: ${value.join(", ")}`;
            }
            return `${key}: ${value}`;
          })
          .join("\n");
      }

      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isClosed = ["Won Close", "Won", "Lost", "Deal Lost", "Deal Confirmation", "Converted Lead", "Converted"].includes(formData.status);

  const getFileIcon = (file) => {
    if (!file) return <FaFileAlt color="#64748b" />;
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".pdf") || file.type?.includes("pdf")) {
      return <FaFilePdf color="#dc2626" size={18} />;
    }
    if (name.endsWith(".doc") || name.endsWith(".docx") || file.type?.includes("word")) {
      return <FaFileWord color="#2563eb" size={18} />;
    }
    if (name.endsWith(".xls") || name.endsWith(".xlsx") || file.type?.includes("sheet") || file.type?.includes("excel")) {
      return <FaFileExcel color="#16a34a" size={18} />;
    }
    if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || file.type?.startsWith("image/")) {
      return <FaFileImage color="#7c3aed" size={18} />;
    }
    return <FaFileAlt color="#64748b" size={18} />;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFormData((prev) => ({
        ...prev,
        doc_file: e.dataTransfer.files[0],
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = () => {
    setFormData((prev) => ({
      ...prev,
      doc_file: null,
      doc_number: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="followup-overlay" onClick={onClose}>
      <div
        className="followup-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="followup-header">
          <div className="followup-header-content">
            <h2 className="followup-title">
              {isEdit ? "Edit" : "Add"} {type === "lead" ? "Lead" : "Deal"} Follow Up
            </h2>
            <p className="followup-subtitle">
              Record communication notes or schedule the next interaction
            </p>
          </div>

          <button
            className="followup-close"
            onClick={onClose}
            aria-label="Close"
          >
            <MdClose />
          </button>
        </div>

        {/* BODY */}
        <div className="followup-body">
          {/* ROW 1: FOLLOW UP TYPE & STATUS */}
          <div className="followup-group">
            <label htmlFor="followup_type">Follow Up Type</label>
            <select
              id="followup_type"
              name="type"
              value={formData.type || "Call"}
              onChange={handleChange}
            >
              <option value="Call">Call</option>
              <option value="Email">Email</option>
              <option value="Meeting">Meeting</option>
              <option value="Demo">Demo</option>
            </select>
          </div>

          <div className="followup-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status || ""}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* ROW 2: ASSIGN TO & NEXT FOLLOW UP DATE */}
          <div className="followup-group">
            <label htmlFor="assign_to">Assign To</label>
            <select
              id="assign_to"
              name="assigned_to"
              value={formData.assigned_to || ""}
              onChange={handleChange}
            >
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || `User #${u.id}`}
                </option>
              ))}
            </select>
          </div>

          {!isClosed ? (
            <>
              <div className="followup-group">
                <label htmlFor="next_followup_date">
                  Next Follow Up Date <span className="text-danger">*</span>
                </label>
                <input
                  id="next_followup_date"
                  type="date"
                  name="next_followup_date"
                  value={formData.next_followup_date || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* ROW 3: NEXT FOLLOW UP TIME */}
              <div className="followup-group">
                <label htmlFor="next_followup_time">
                  Next Follow Up Time
                </label>
                <input
                  id="next_followup_time"
                  type="time"
                  name="next_followup_time"
                  value={formData.next_followup_time || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Placeholder space for 2nd column of row 3 */}
              <div className="followup-group" style={{ visibility: "hidden" }} />
            </>
          ) : null}

          {/* ROW 3: ACTUAL EXPENSE (ONLY WHEN WON CLOSE) */}
          {(formData.status === "Won Close" || formData.status === "Won") && (
            <div className="followup-group followup-full">
              <label htmlFor="actual_expense">
                Actual Expense
              </label>
              <input
                id="actual_expense"
                type="text"
                name="actual_expense"
                value={formData.actual_expense ? Number(formData.actual_expense).toLocaleString("en-IN") : ""}
                onChange={handleChange}
                placeholder="e.g. 20,000"
                required
              />
            </div>
          )}

          {/* ROW 4: REMARKS / LOST REASON */}
          <div className="followup-group followup-full">
            {(formData.status === "Lost" || formData.status === "Deal Lost") ? (
              <>
                <label htmlFor="lost_reason">
                  Lost Reason
                </label>
                <input
                  id="lost_reason"
                  type="text"
                  name="remarks"
                  value={formData.remarks || ""}
                  onChange={handleChange}
                  placeholder="Enter reason for lost..."
                />
              </>
            ) : (
              <>
                <label htmlFor="remarks">Remarks</label>
                <textarea
                  id="remarks"
                  name="remarks"
                  rows="3"
                  value={formData.remarks || ""}
                  onChange={handleChange}
                  placeholder="Enter follow up remarks..."
                />
              </>
            )}
          </div>

          {/* ROW 5: OPTIONAL DOCUMENT ATTACHMENT FOR DEALS */}
          {type === "deal" && (
            <div className="followup-doc-section followup-full">
              <div className="followup-doc-header">
                <div className="followup-doc-title">
                  <FaPaperclip />
                  <span>Attach PO / Invoice / Document (Optional)</span>
                </div>
                {formData.doc_file && (
                  <button
                    type="button"
                    className="followup-doc-clear-btn"
                    onClick={removeFile}
                    title="Remove attached document"
                  >
                    <FaTrashAlt size={10} style={{ marginRight: 4 }} />
                    Remove File
                  </button>
                )}
              </div>

              <div className="followup-doc-grid">
                <div className="followup-group">
                  <label htmlFor="doc_type">Document Type</label>
                  <select
                    id="doc_type"
                    name="doc_type"
                    value={formData.doc_type || "PO"}
                    onChange={handleChange}
                  >
                    <option value="PO">Purchase Order (PO)</option>
                    <option value="INVOICE">Client Invoice</option>
                    <option value="PROPOSAL">Proposal</option>
                    <option value="CONTRACT">Contract</option>
                    <option value="OTHER">Other Document</option>
                  </select>
                </div>

                <div className="followup-group">
                  <label htmlFor="doc_number">
                    {formData.doc_type === "INVOICE" ? "Invoice Number" : "PO / Doc Number"}
                  </label>
                  <input
                    id="doc_number"
                    type="text"
                    name="doc_number"
                    value={formData.doc_number || ""}
                    onChange={handleChange}
                    placeholder={formData.doc_type === "INVOICE" ? "e.g. INV-8801" : "e.g. PO-2026-001"}
                  />
                </div>

                <div className="followup-group followup-full">
                  <label htmlFor="doc_file">Upload File (PDF, Word, Excel, Image)</label>

                  {!formData.doc_file ? (
                    <div
                      className={`followup-dropzone ${isDragging ? "dragging" : ""}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        id="doc_file"
                        type="file"
                        name="doc_file"
                        aria-label="Upload File (PDF, Word, Excel, Image)"
                        style={{ display: "none" }}
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
                      />
                      <FaFileUpload size={22} className="followup-dropzone-icon" />
                      <div className="followup-dropzone-text">
                        <strong>Click to browse</strong> or drag & drop file
                      </div>
                      <div className="followup-dropzone-sub">
                        PDF, Word, Excel, Images up to 25MB
                      </div>
                    </div>
                  ) : (
                    <div className="followup-file-selected-card">
                      <div className="followup-file-card-left">
                        <div className="followup-file-icon-box">
                          {getFileIcon(formData.doc_file)}
                        </div>
                        <div className="followup-file-meta">
                          <span className="followup-file-name" title={formData.doc_file.name}>
                            {formData.doc_file.name}
                          </span>
                          <span className="followup-file-size">
                            {(formData.doc_file.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="followup-file-replace-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Replace
                      </button>
                      <input
                        ref={fileInputRef}
                        id="doc_file"
                        type="file"
                        name="doc_file"
                        aria-label="Upload File (PDF, Word, Excel, Image)"
                        style={{ display: "none" }}
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="followup-footer">
          <button
            className="followup-btn followup-btn--secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="followup-btn followup-btn--primary"
            onClick={saveFollowup}
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : type === "lead" && formData.status === "Deal Confirmation"
              ? "Next"
              : "Save Follow Up"}
          </button>
        </div>
      </div>
    </div>
  );
}